import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import Link from 'next/link';

import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { TextButton } from '@flipster/react/design-system/text-button';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import { css } from '@flipster/styled-system/css';
import { circle, hstack, vstack } from '@flipster/styled-system/patterns';
import { numberFormatter } from '@flipster/utils/num';

import { RewardImage } from '../_misc/RewardImage';
import { bidInfoBunja, countdownAreaBunja, rewardImageAreaBunja } from './bunja';

const BidInfo = () => {
  const t = useTranslations();
  const { bidInfoAtom, trackAvailableSparksClick, openBidModalWithTextLink, openBidModalWithButton } =
    useBunja(bidInfoBunja);
  const { highestBidder, me, myAvailableTotalSparks, isFirstBid } = useAtomValue(bidInfoAtom);

  return (
    <div
      className={vstack({ w: { base: '100%', tablet: 520 }, mt: 24, alignSelf: 'center', textStyle: 'Text/M/Regular' })}
    >
      <RewardImageArea />
      <CountdownArea />
      <div className={hstack({ w: '100%', px: 8, py: 16, borderRadius: 8, bg: 'background.default.level-1' })}>
        <div className={vstack({ flexBasis: 0, flexGrow: 1, color: 'content.default.level-5' })}>
          <span className={css({ textStyle: 'Text/S/Regular' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Highest_bid' />
          </span>
          {highestBidder ? (
            <>
              <span className={css({ mt: 2, textStyle: 'Title/M' })}>{numberFormatter(highestBidder.amount)}</span>
              <span className={css({ mt: 2, textStyle: 'Text/S/Strong' })}>
                {highestBidder.isMe ? t('Autogen_common.r251126.s_You') : highestBidder.nickname}
              </span>
            </>
          ) : (
            <span className={css({ mt: 8, textStyle: 'Text/M/Strong', color: 'content.default.level-1' })}>
              <FormattedMessage id='Autogen_common.r251126.s_No_bids_yet' />
            </span>
          )}
        </div>
        <span className={css({ px: 8, textStyle: 'Title/XS', color: 'content.default.level-1' })}>VS</span>
        <div className={vstack({ flexBasis: 0, flexGrow: 1, color: 'content.default.level-2' })}>
          <span className={css({ textStyle: 'Text/S/Regular' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Your_bid' />
          </span>
          {me.amount > 0 ? (
            <>
              <span className={css({ mt: 2, textStyle: 'Title/M' })}>{numberFormatter(me.amount)}</span>
              <span className={css({ mt: 2, textStyle: 'Text/S/Strong' })}>{me.nickname}</span>
            </>
          ) : (
            <TextButton
              label={t('Autogen_common.r251126.s_Place_your_bid')}
              css={{ mt: 8 }}
              onClick={openBidModalWithTextLink}
            />
          )}
        </div>
      </div>
      <Link
        href='/spark/reward'
        className={hstack({
          justifyContent: 'space-between',
          py: 16,
          w: '100%',
          borderBottom: '1px solid {colors.border.default.level-1}',
          color: 'content.default.level-5',
        })}
        onClick={trackAvailableSparksClick}
      >
        <div>
          <FormattedMessage id='Autogen_common.r251126.s_Your_available_total_Sparks' />
        </div>
        <div className={hstack({ gap: 4 })}>
          <div>{numberFormatter(myAvailableTotalSparks)}</div>
          <ChevronRightIcon size={16} />
        </div>
      </Link>
      <Button
        css={{ mt: 24, w: 345 }}
        hierarchy='primary'
        color='accent'
        size='large'
        label={isFirstBid ? 'Place bid' : 'Add to increase bid'}
        onClick={openBidModalWithButton}
      />
    </div>
  );
};

export default BidInfo;

const RewardImageArea = () => {
  const { imageUrlAtom, rewardBlurAtom } = useBunja(rewardImageAreaBunja);
  const imageUrl = useAtomValue(imageUrlAtom);
  const rewardBlur = useAtomValue(rewardBlurAtom);
  return <RewardImage imageUrl={imageUrl} blur={rewardBlur} />;
};

const CountdownArea = () => {
  const { countdownMessageAtom } = useBunja(countdownAreaBunja);
  const countdownMessage = useAtomValue(countdownMessageAtom);
  return (
    <div className={hstack({ gap: 8, mt: { base: 32, tablet: 40 }, mb: 4, color: 'content.default.level-2' })}>
      <span className={circle({ w: 4, h: 4, bg: 'content.state.positive' })} />
      <span>
        Live auction ends in{' '}
        <span
          className={css({
            textStyle: 'Text/M/Strong',
            color: 'content.default.level-5',
            fontVariantNumeric: 'tabular-nums',
          })}
        >
          {countdownMessage}
        </span>
      </span>
    </div>
  );
};
